import { useState, Fragment, useEffect } from 'react';

import ProjectRow from "./ProjectTable/projectRow";
import SearchBar from '../Common/searchBar';

import { API } from "../../../Common/lib/api";
import ResponseNotification from "../UserManagement/Modals/responseNotification";

import Pagination from '../Common/pagination';

/**
 * Returns the Project Approval page component.
 * 
 * @returns Project Approval page component.
 */
export default function ProjectApprovalTable() {
  const [searchTerm, setSearchTerm] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [noSearchResults, setNoSearchResults] = useState(true);

  const [currentPage, setCurrentPage] = useState(0);
  const [projectPerPage, setProjectPerPage] = useState(10);

  const [notificationShow, setNotificationShow] = useState(false);
  const [notificationPurpose, setNotificationPurpose] = useState("");

  const [projects, setProjects] = useState([]);
  const [projectCount, setProjectCount] = useState(0);
  const [projectPublishStatus, setProjectPublishStatus] = useState({
    projectId: "",
    newPublishStatus: "",
  });

  const [notification, setNotification] = useState({
    status: false,
    title: "",
    message: "",
  });

  useEffect(() => {
    getProjects();
    setProjectCount(0);
  }, [currentPage]);

  /**
   * Gets all of the ARF Projects.
   */
  const getProjects = async () => {
    try {
      const filter = {
        publishStatus: "under review"
      };

      if (searchTerm.length > 0) {
        filter.q = searchTerm;
      }

      const projectsQuery = await API.projects.search(filter, {
        from: currentPage * projectPerPage,
        limit: projectPerPage
      });

      if (projectsQuery.data.searchProjects.total != 0) {
        setNoSearchResults(false);
        setProjects(projectsQuery.data.searchProjects.items);
        setProjectCount(projectsQuery.data.searchProjects.total);
      } else {
        setNoSearchResults(true);
        setProjects([]);
        setProjectCount(0);
      }

      setPageLoading(false);
    } catch (e) {
      setPageLoading(false);
      console.error("Error fetching projects:", e);
    }
  };


  /**
   * Returns the UNIX EPOCH timestamp in milliseconds into a formatted MM/dd/YYYY date. 
   *
   * @param {Integer} unix 
   * @returns The UNIX EPOCH timestamp in milliseconds into a formatted MM/dd/YYYY date. 
   */
  const formatUNIX = (unix) => {
    return new Date(unix).toLocaleDateString("en-US");
  };

  /**
   * Handles the selected project publishStatus attribute.
   * 
   * @param {String} projectId 
   * @param {String} publishStatus 
   */
  const projectPublishStatusChange = (projectId, publishStatus) => {
    setProjectPublishStatus({
      projectId: projectId,
      newPublishStatus: publishStatus,
    });
  };

  /**
   * Handles the project search input field.
   * 
   * @param {Event Handles} event 
   */
  const handleSearch = (event) => {
    setSearchTerm((event.target.value).trim());
  };

  /**
   * Updates the selected Project with the new DOI value.
   */
  const updateProjectInfo = async () => {
    setNotificationShow(false);
    setNotification({
      status: false,
      title: "",
      message: "",
    });

    try {
      if (!projectPublishStatus.newPublishStatus) {
        setNotificationShow(true);
        setNotificationPurpose("DOI");
        setNotification({
          status: false,
          title: "An Error has occurred",
          message: `Project DOI could not be saved!`
        });
        return;
      }

      const updateData = {
        id: projectPublishStatus.projectId,
        publishStatus: projectPublishStatus.newPublishStatus,
      };

      if (projectPublishStatus.newPublishStatus === "rejected") {
        updateData.attachmentKeys = "[]";
      }

      const result = await API.projects.update(updateData);
      const updatedProject = result.data.updateProject;

      const updatedProjects = projects.filter(project => {
        return project.id !== updatedProject.id;
      });

      setProjects(updatedProjects);
      
      setNotificationShow(true);
      setNotificationPurpose("Status");
      setNotification({
        status: true,
        title: "Project Status Successfully Updated",
        message: `Project status has been set to ${projectPublishStatus.newPublishStatus}!`
      });
    } catch (error) {
      console.error(error);
      setNotificationShow(true);
      setNotificationPurpose("Status");
      setNotification({
        status: false,
        title: "An Error has occurred",
        message: `Project status could not be updated!`
      });
    }
  };

  return (
    <>
      <div className="">
        <div className="px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
          
          <div className="">
            <ResponseNotification
              as={Fragment}
              show={notificationShow}
              notification={notification}
              setShow={setNotificationShow}
              notificationPurpose={notificationPurpose}
            />

            <div className="py-6 px-3 w-full md:px-5 md:py-12 bg-white md:w-full rounded-lg xl:p-12 shadow-sm border border-gray-200">
              <main className="mt-2 overflow-hidden">
              <div className="flex justify-between w-full">
                <h1 className="text-2xl py-2 font-bold leading-tight text-gray-900">
                  Project Approval
                </h1>
                <SearchBar
                  searchPurpose={"doi"}
                  searchTerm={searchTerm}
                  currentPage={currentPage}
                  getProjects={getProjects}
                  handleSearch={handleSearch}
                  setCurrentPage={setCurrentPage}
                />
              </div>

                {pageLoading ? (
                  <div className="text-base p-40 bg-gray-100 place-content-center">
                    <p className="text-center font-medium">
                      Loading
                    </p>
                  </div>
                ) : (
                  <>

                    {noSearchResults ? (
                      <div className="text-base p-40 bg-gray-100 place-content-center">
                        <p className="text-center font-medium">
                          No Projects Found!
                        </p>
                      </div>
                    ) : (
                      <>
                        <table className="w-full">
                          <thead className="bg-gray-100">
                            <tr>
                              <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:text-base sm:pl-6">
                                Project Name
                              </th>

                              <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:text-base sm:table-cell">
                                Created By
                              </th>

                              <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:text-base sm:table-cell">
                                Date
                              </th>

                              <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                                <span className="sr-only">Action</span>
                              </th>
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-gray-200 bg-white">
                            {projects.map((project) => (
                              <ProjectRow
                                key={project.id}
                                project={project}
                                formatUNIX={formatUNIX}
                                projectPublishStatusChange={projectPublishStatusChange}
                                updateProjectInfo={updateProjectInfo}
                              />
                            ))}
                          </tbody>
                        </table>

                        <Pagination
                          projectsPerPage={projectPerPage}
                          currentPage={currentPage}
                          projectCount={projectCount}
                          setCurrentPage={setCurrentPage}
                        />
                      </>
                    )}
                  </>
                )}
              </main>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};